import { useCallback } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	type OperationalEvent,
	useOperationalAnalytics,
} from '../analytics/use-operational-analytics';
import { type Edition, type Product } from '../types';

import { type useOnAttemptToUpgradeQuery } from './__generated__/useOnAttemptToUpgradeQuery.graphql';
import { isProductOneOf } from './is-product-one-of';
import { getHamsKey } from './product-to-hams-key';

// Equates to ~4 minutes of attempts spread out between the delay of waiting for the response
export const MAX_ATTEMPTS = 48;
const TIME_BETWEEN_ATTEMPTS_MS = 5_000; // 5 seconds

async function getHasJiraUpgradedFromFree(product: Product) {
	// TODO: Add the query for GUPs to find out if the customer is on a manual trial
	const response = await fetch('/rest/internal/latest/meta/edition');

	if (!response.ok) {
		throw new Error(`Fetching upgrade status failed with ${response.status}`);
	}

	const data = (await response.json()) as Partial<
		Record<string, 'free' | 'standard' | 'premium' | unknown>
	>;

	const key = getHamsKey(product);

	return data[key] === 'standard' || data[key] === 'premium';
}

async function getHasConfluenceUpgradedFromFree(
	environment: RelayModernEnvironment,
): Promise<boolean> {
	const response = await fetchQuery<useOnAttemptToUpgradeQuery>(
		environment,
		graphql`
			query useOnAttemptToUpgradeQuery {
				# TODO: Add the query for GUPs to find out if the customer is on a manual trial
				confluence_tenantContext {
					editions {
						confluence
					}
				}
			}
		`,
		{},
	).toPromise();

	const confluenceEdition = response?.confluence_tenantContext?.editions?.confluence;

	return Boolean(
		confluenceEdition && (confluenceEdition === 'STANDARD' || confluenceEdition === 'PREMIUM'),
	);
}

async function getHasProductUpgraded(
	product: Product,
	environment: RelayModernEnvironment,
): Promise<boolean> {
	if (product === 'confluence') {
		return getHasConfluenceUpgradedFromFree(environment);
	}

	if (isProductOneOf(product, ['jira', 'jira-product-discovery', 'jira-service-management'])) {
		return getHasJiraUpgradedFromFree(product);
	}

	throw new Error(`Unsupported product ${product}`);
}

async function pollForUpgrade(
	environment: RelayModernEnvironment,
	product: Product,
	sendAnalyticsEvent: (event: OperationalEvent) => void,
): Promise<boolean> {
	const sendAnalytics = (action: string, additionalAttributes: Record<string, unknown> = {}) => {
		sendAnalyticsEvent({
			action,
			actionSubject: 'pollForUpgrade',
			actionSubjectId: 'editionAwareness',
			attributes: {
				...additionalAttributes,
			},
		});
	};

	const waitForInterval = () =>
		new Promise((resolve) => setTimeout(resolve, TIME_BETWEEN_ATTEMPTS_MS));

	sendAnalytics('started');

	for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
		try {
			const hasUpgraded = await getHasProductUpgraded(product, environment);

			if (hasUpgraded) {
				sendAnalytics('success', { attempt: attempts + 1 });
				return true;
			}

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		} catch (error) {
			sendAnalytics('error', {
				attempt: attempts + 1,
				error: error instanceof Error ? error.message : 'Unknown error',
			});

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		}
	}

	sendAnalytics('timeout', { attempts: MAX_ATTEMPTS });

	return false;
}

export function useOnAttemptToUpgrade(product: Product, edition: Edition) {
	const sendAnalyticsEvent = useOperationalAnalytics({ product, edition });

	const environment = useRelayEnvironment();

	const startCheckingForAttemptToUpgrade = useCallback(async () => {
		if (!fg('ea_upgrade_attempt_analytics')) {
			// Generic catch to ensure we don't run this if the gate is off
			return false;
		}

		const hasUpgraded = await pollForUpgrade(environment, product, sendAnalyticsEvent);

		// At a later stage this is where a flag would be shown to the customer, informing them they have upgraded

		return hasUpgraded;
	}, [environment, product, sendAnalyticsEvent]);

	// At a later stage there will be a useEffect which checks if the customer has started a manual trial but is not on
	// a trial yet (aka in product doesn't reflect the action they performed). This polling will show a flag too

	return { startCheckingForAttemptToUpgrade };
}
